import { IController } from 'angular';
import { each, filter, findIndex, map, reduce } from 'lodash';
import { ImUser } from 'Src/ng2/shared/services/im-models/im-user';

import { UtilitiesService } from 'src/ng2/shared/services/utilities/utilities.service';
import { UserRoleTypeToAccessLevelMapping } from '../../../ng2/shared/constants/manage-users.constant';
import { ISchool, TValidSchoolTypes } from '../../../ng2/shared/typings/interfaces/school.interface';
import { IUser } from '../../../ng2/shared/typings/interfaces/user.interface';
import { TValidUserRoles } from '../../../ng2/shared/typings/interfaces/user.interface';

export interface IManageUsersUser {
  _id: string;
  picture: string;
  name: string;
  doeEmail: string;
  jobRole: string;
  googleEmail: string;
  accessLevel: string;
  filtered: boolean;
  selected: boolean;
  editable: boolean;
  assignmentDescription: string;
  isSchoolTrustedUser: boolean;
}

export interface IManageUsersSchool {
  schoolId: string;
  schoolType: TValidSchoolTypes;
  domainName: string;
}

export interface IManageUsersSharedState {
  atLeastOneUserIsSelected: boolean;
}

interface IHashedFilteredSelectedAndEditable {
  filteredHash: {
    [id: string]: boolean;
  };
  selectedHash: {
    [id: string]: boolean;
  };
  editableHash: {
    [id: string]: boolean;
  };
}

/* istanbul ignore next */
export class ManageUsersCtrl implements IController {
  static $inject: string[] = ['SpinnerModal', 'User', 'UtilitiesService', 'ImUser'];

  // bindings
  currentSchool: ISchool;
  schoolUsers: IUser[];
  currentUser;

  // class properties
  viewingUserRoleType: TValidUserRoles;
  users: IManageUsersUser[];
  school: IManageUsersSchool;
  sharedState: IManageUsersSharedState;

  constructor(private SpinnerModal, private User, private utilitiesService: UtilitiesService, private imUser: ImUser) {}

  $onInit() {
    // properties shared among child components to determine state
    this.sharedState = {
      atLeastOneUserIsSelected: false,
    };

    this.viewingUserRoleType = this.imUser.getRoleType(this.currentUser);
    this.users = this.getManageUsersUsers(this.schoolUsers, this.viewingUserRoleType);

    this.school = this.getManageUsersSchool(this.currentSchool);
    this.SpinnerModal.deactivateSpinner();
  }

  public addNewSchoolUser(schoolUser): void {
    const _schoolUser = new this.User(this.utilitiesService.copyPOJO(schoolUser));
    this.schoolUsers.unshift(_schoolUser);

    const users = this.getManageUsersUsers(this.schoolUsers, this.viewingUserRoleType);
    this.users = users;
  }

  // this shapes the user used in the child components
  private mapSchoolUser(
    hashedFilteredSelectedAndEditable: IHashedFilteredSelectedAndEditable,
    viewingUserRoleType: TValidUserRoles,
    schoolUser,
  ): IManageUsersUser {
    const { _id, picture, doeEmail, jobRole: _jobRole, gafeEmail, sourceMetaData, isSchoolTrustedUser } = schoolUser;

    const name = this.imUser.getFullName(schoolUser);
    const jobRole = _jobRole.join(', ');
    const googleEmail = gafeEmail;
    const accessLevel = this.getSchoolUserAccessLevelString(schoolUser);
    // empty string, instead of `null`, makes assignmentDescription easier to sort
    const assignmentDescription = sourceMetaData.assignmentDescription || '';
    let filtered;
    let selected;
    let editable;

    if (hashedFilteredSelectedAndEditable) {
      const { filteredHash, selectedHash, editableHash } = hashedFilteredSelectedAndEditable;

      filtered = typeof filteredHash[_id] !== 'undefined' ? filteredHash[_id] : true;
      selected = typeof selectedHash[_id] !== 'undefined' ? selectedHash[_id] : false;
      editable =
        typeof editableHash[_id] !== 'undefined'
          ? editableHash[_id]
          : this.canEditPermissionsForUserAccessLevel(accessLevel, viewingUserRoleType);
    } else {
      filtered = true;
      selected = false;
      editable = this.canEditPermissionsForUserAccessLevel(accessLevel, viewingUserRoleType);
    }

    return {
      _id,
      picture,
      name,
      doeEmail,
      jobRole,
      googleEmail,
      accessLevel,
      assignmentDescription,
      isSchoolTrustedUser,
      filtered,
      selected,
      editable,
    };
  }

  private getHashedFilteredSelectedAndEditable(users: IManageUsersUser[]): IHashedFilteredSelectedAndEditable {
    const acc: IHashedFilteredSelectedAndEditable = {
      filteredHash: {},
      selectedHash: {},
      editableHash: {},
    };

    reduce(
      users,
      (acc, u) => {
        const id = u._id;

        acc.filteredHash[id] = u.filtered;
        acc.selectedHash[id] = u.selected;
        acc.editableHash[id] = u.editable;

        return acc;
      },
      acc,
    );

    return acc;
  }

  public getSelectedUsers(): IManageUsersUser[] {
    const selectedUsers = filter(this.users, u => {
      return this.isUserSelected(u);
    });

    return selectedUsers;
  }

  public isUserSelected(user: IManageUsersUser) {
    return user.filtered && user.editable && user.selected;
  }

  public updateUsersDelegatedRole(ids, delegatedRole) {
    const idsHash = reduce(
      ids,
      (acc: object, id: string) => {
        acc[id] = true;

        return acc;
      },
      {},
    );

    each(this.schoolUsers, (u: any) => {
      const userId = u._id;

      if (idsHash[userId]) {
        u.delegatedRole = delegatedRole;
      }
    });

    const users = this.getManageUsersUsers(this.schoolUsers, this.viewingUserRoleType);
    this.users = users;
  }

  public updateSchoolUser(updatedSchoolUser: any) {
    // replace old user
    const { _id } = updatedSchoolUser;
    const indxOfOldUser = findIndex(this.schoolUsers, { _id });
    this.schoolUsers[indxOfOldUser] = updatedSchoolUser;

    // map new users
    const users = this.getManageUsersUsers(this.schoolUsers, this.viewingUserRoleType);
    this.users = users;
  }

  private canEditPermissionsForUserAccessLevel(accessLevel: string, viewingUserRoleType: TValidUserRoles) {
    let editable = true;

    if (accessLevel === 'School Admin') {
      editable = false;
    }
    // TODO: add the else statement back if it is determined that a 'delegated_school_admin'
    // should not be able to edit other 'delegated_school_admin'

    // else {
    //   const userRoleType = UserAccessLevelToRoleTypeMapping[accessLevel];
    //   const bothAreDelegatedAdmins = viewingUserRoleType === 'delegated_school_admin' &&
    //     userRoleType === 'delegated_school_admin';

    //   editable = bothAreDelegatedAdmins ? false : true;
    // }

    return editable;
  }

  private getManageUsersUsers(schoolUsers: IUser[], viewingUserRoleType: TValidUserRoles) {
    let hashedFilteredSelectedAndEditable;

    if (this.users) {
      hashedFilteredSelectedAndEditable = this.getHashedFilteredSelectedAndEditable(this.users);
    }

    const users: IManageUsersUser[] = map(
      this.schoolUsers,
      this.mapSchoolUser.bind(this, hashedFilteredSelectedAndEditable, viewingUserRoleType),
    );

    return users;
  }

  private getManageUsersSchool(currentSchool): IManageUsersSchool {
    const school: IManageUsersSchool = {
      schoolId: currentSchool._id,
      schoolType: currentSchool.schoolType,
      domainName: currentSchool.gafeDetails.domainName,
    };

    return school;
  }

  private getSchoolUserAccessLevelString(schoolUser) {
    const roleType = this.imUser.getRoleType(schoolUser);

    return UserRoleTypeToAccessLevelMapping[roleType];
  }
}
